/**
 * @fileoverview Export bundling for report annexures (V1)
 *
 * When a report has uploaded annexure files, exporting it should include them.
 * Since annexures can be any file type (PDF, DOCX, images), the robust,
 * type-agnostic V1 approach is to ship a ZIP: the exported report document
 * plus an `Annexures/` folder with each uploaded file.
 *
 * Used by: ReportingView export handlers.
 */

/** One annexure file to bundle into an export. */
export interface ExportAnnexure {
  /** The annexure section title (used for the file name in the zip). */
  title: string;
  /** Original uploaded file name (keeps the correct extension). */
  fileName: string;
  /** Signed storage URL to fetch the file bytes from. */
  url: string;
}

/** Strip characters that are illegal in file names across OSes. */
function sanitizeFileName(name: string): string {
  return name.replace(/[\\/:*?"<>|]+/g, "-").trim() || "annexure";
}

/**
 * Bundle the exported report document together with its annexure files into a
 * single ZIP. Returns the report unchanged when there are no annexures.
 *
 * @param report - The exported report document (blob + file name).
 * @param annexures - The annexure files to include.
 * @param zipBaseName - Base name for the zip (without extension).
 * @returns The blob + file name to download.
 *
 * @example
 * const out = await bundleReportWithAnnexures(
 *   { blob: pdfBlob, fileName: "Report.pdf" },
 *   annexures,
 *   "Report (with annexures)",
 * );
 * downloadBlob(out.blob, out.fileName);
 */
export async function bundleReportWithAnnexures(
  report: { blob: Blob; fileName: string },
  annexures: ExportAnnexure[],
  zipBaseName: string,
): Promise<{ blob: Blob; fileName: string }> {
  if (annexures.length === 0) return report;

  const JSZip = (await import("jszip")).default;
  const zip = new JSZip();
  zip.file(report.fileName, report.blob);
  const folder = zip.folder("Annexures");

  let index = 1;
  for (const annexure of annexures) {
    const response = await fetch(annexure.url);
    if (!response.ok) continue;
    const bytes = await response.arrayBuffer();
    const prefix = String(index).padStart(2, "0");
    folder?.file(
      `${prefix} ${sanitizeFileName(annexure.title)} - ${sanitizeFileName(annexure.fileName)}`,
      bytes,
    );
    index += 1;
  }

  const blob = await zip.generateAsync({ type: "blob" });
  return { blob, fileName: `${sanitizeFileName(zipBaseName)}.zip` };
}
